import { Callout, HeaderText } from "@/components/ui";
import { usePinnedPatients } from "@/features/Messages/network/usePinnedPatients";
import { ForceStartLiveChat } from "./ForceStartLiveChat";
import { NoriOutreachButton } from "./NoriOutreachButton";
import { StartNote } from "./StartNote";
import {
  AdministrativeInfo,
  ClinicalInfo,
  DemographicsInfo,
  EnrolledPrograms,
  EnrollmentVisitChip,
  MembershipInfo,
  PatientAllergiesDisplay,
  PatientAvatar,
  PatientBarSkeleton,
  ProviderFeedback,
  RoutineMessageActions,
} from "./components";
import { usePatientBarData } from "./hooks/usePatientBarData";
import type { PatientDemographics } from "./types/patientDemographics";

interface PatientBarProps {
  patientId: string;
  encounterId?: string;
}

export const PatientBar = ({ patientId, encounterId }: PatientBarProps) => {
  const {
    demographics,
    allergies,
    limitedAccess,
    coreVitals,
    isLoading,
    hasDemographicsError,
    hasAllergiesError,
    refetchDemographics,
  } = usePatientBarData(patientId);

  const { data: pinnedPatients } = usePinnedPatients();
  const isPinned = pinnedPatients?.patientIds.includes(patientId) ?? false;

  const {
    demographics: demo,
    administrative,
    clinical,
    membership,
    enrolledPrograms,
  } = demographics?.data || ({} as PatientDemographics);

  const patientPreferredName =
    demo?.preferredName !== demo?.nameFirst ? demo?.preferredName : "";
  const patientName = `${demo?.nameFirst} ${patientPreferredName ? `"${patientPreferredName}"` : ""} ${demo?.nameLast}`;

  // Only show error state if we can't load demographics (most critical data)
  if (hasDemographicsError) {
    return (
      <div className="bg-primary p-2">
        <Callout
          className="w-full cursor-pointer"
          variant="danger"
          title="Unable to load demographic data!"
          description="Please click here to try again."
          onClick={() => refetchDemographics()}
        />
      </div>
    );
  }

  if (isLoading) {
    return <PatientBarSkeleton />;
  }

  return (
    <div className="flex items-center justify-between bg-primary">
      <div className="flex items-center gap-3 p-4">
        <PatientAvatar name={patientName} />
        <HeaderText size="xl">{patientName}</HeaderText>

        <DemographicsInfo demographics={demo} vitals={coreVitals?.data} />
        <AdministrativeInfo administrative={administrative} />
        <ClinicalInfo clinical={clinical} />
        <PatientAllergiesDisplay
          allergiesData={allergies?.data}
          isError={hasAllergiesError}
        />
        <RoutineMessageActions patientId={patientId} isPinned={isPinned} />
        <MembershipInfo
          hieOptIn={administrative?.hieOptedIn}
          limitedAccess={limitedAccess.data?.limitedAccess}
          membership={membership}
          preferredLanguage={demo?.preferredLanguage}
          careNotEstablished={administrative?.careNotEstablished}
        />
        <EnrolledPrograms enrolledPrograms={enrolledPrograms ?? []} />
        <EnrollmentVisitChip patientId={patientId} encounterId={encounterId} />
      </div>
      <div className="flex items-center gap-3 p-4">
        <ForceStartLiveChat />
        <StartNote />
        <NoriOutreachButton />
        <ProviderFeedback administrative={administrative} />
      </div>
    </div>
  );
};
